import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { losBannedAppraisersApi } from "../../api/losBannedAppraisersApi";

/**
 * Checks an appraiser license against the global banned-appraisers registry
 * (GET /loan-processing/banned-appraisers/check) and, when banned, renders a
 * hard warning with the match details so the reviewer rejects the valuation.
 * Renders nothing when there's no license or the appraiser isn't banned.
 */
const BannedAppraiserFlag = ({ license, sx }) => {
  const [match, setMatch] = useState(null);

  useEffect(() => {
    let alive = true;
    const lic = (license || "").trim();
    setMatch(null);
    if (!lic) return undefined;
    losBannedAppraisersApi
      .check(lic)
      .then((res) => {
        if (alive && res?.banned) setMatch(res.match || {});
      })
      .catch(() => {
        /* best-effort signal; never block the review UI on a failed check */
      });
    return () => {
      alive = false;
    };
  }, [license]);

  if (!match) return null;

  const name =
    [match.firstName, match.lastName].filter(Boolean).join(" ") ||
    match.company ||
    "This appraiser";
  const loans = Array.isArray(match.evLqLoansAffected)
    ? match.evLqLoansAffected
    : [];

  return (
    <Box
      sx={{
        border: "1px solid #fecaca",
        background: "#fef2f2",
        borderRadius: "10px",
        p: 1.5,
        ...sx,
      }}
    >
      <Flx ac g={1}>
        <WarningAmberRounded sx={{ color: "#b91c1c", fontSize: 20 }} />
        <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#b91c1c" }}>
          Banned appraiser — {name} (license {license})
        </Txt>
      </Flx>
      <Box sx={{ pl: "28px", mt: 0.5 }}>
        {match.bannedReason && (
          <Txt sx={{ fontSize: 12, color: "#7f1d1d" }}>
            Reason: {match.bannedReason}
          </Txt>
        )}
        {match.bannedEffectiveDate && (
          <Txt sx={{ fontSize: 12, color: "#7f1d1d" }}>
            Banned since {match.bannedEffectiveDate}
          </Txt>
        )}
        {loans.length > 0 && (
          <Txt sx={{ fontSize: 12, color: "#7f1d1d" }}>
            Already touched: {loans.join(", ")}
          </Txt>
        )}
        <Txt sx={{ fontSize: 12, color: "#7f1d1d", mt: 0.25, fontWeight: 600 }}>
          Reject this valuation.
        </Txt>
      </Box>
    </Box>
  );
};

export default BannedAppraiserFlag;
